import { useState, useEffect, type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  Menu, X, ChevronDown, LogIn,
  Building2, ShieldCheck, Plane, Car,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import type { SupportedLanguage } from '@/i18n'

interface NavChild {
  labelKey: string
  to: string
  icon?: ComponentType<{ className?: string }>
}

interface NavItem {
  labelKey: string
  to: string
  children?: NavChild[]
}

const navItems: NavItem[] = [
  {
    labelKey: 'nav.about',
    to: 'chi-siamo',
  },
  {
    labelKey: 'nav.products',
    to: 'prodotti',
  },
  {
    labelKey: 'nav.sectors',
    to: 'settori',
    children: [
      { labelKey: 'nav.police', to: 'settori/polizia-locale', icon: Building2 },
      { labelKey: 'nav.lawEnforcement', to: 'settori/forze-ordine', icon: ShieldCheck },
      { labelKey: 'nav.portsAirports', to: 'settori/porti-aeroporti', icon: Plane },
      { labelKey: 'nav.parking', to: 'settori/parking', icon: Car },
    ],
  },
  { labelKey: 'nav.academy', to: 'academy' },

  { labelKey: 'nav.contacts', to: 'contatti' },
]

/** Map Italian slugs to English equivalents for navigation */
const navSlugMap: Record<string, Record<SupportedLanguage, string>> = {
  'chi-siamo': { it: 'chi-siamo', en: 'about-us' },
  'prodotti': { it: 'prodotti', en: 'products' },
  'settori': { it: 'settori', en: 'sectors' },
  'settori/polizia-locale': { it: 'settori/polizia-locale', en: 'sectors/local-police' },
  'settori/forze-ordine': { it: 'settori/forze-ordine', en: 'sectors/law-enforcement' },
  'settori/porti-aeroporti': { it: 'settori/porti-aeroporti', en: 'sectors/ports-airports' },
  'settori/parking': { it: 'settori/parking', en: 'sectors/parking' },
  'academy': { it: 'academy', en: 'academy' },

  'contatti': { it: 'contatti', en: 'contacts' },
  'accedi': { it: 'accedi', en: 'login' },
}

function getLocalizedNavPath(to: string, lang: SupportedLanguage): string {
  const mapping = navSlugMap[to]
  return mapping ? mapping[lang] : to
}

// Custom cubic bezier for smooth ease-out
const smoothEase: [number, number, number, number] = [0.23, 1, 0.32, 1]

// Animation variants for dropdown container
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: 8,
    scale: 0.96,
    transition: { duration: 0.15 },
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: smoothEase,
      staggerChildren: 0.04,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: 4,
    scale: 0.98,
    transition: { duration: 0.12 },
  },
}

// Animation variants for each dropdown item
const dropdownItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: smoothEase },
  },
  exit: { opacity: 0, x: -4, transition: { duration: 0.1 } },
}

/** Check if a nav item (or any of its children) matches the current path */
function isNavItemActive(item: NavItem, pathname: string, lang: SupportedLanguage): boolean {
  const localizedPath = `/${lang}/${getLocalizedNavPath(item.to, lang)}`
  if (pathname === localizedPath || pathname.startsWith(localizedPath + '/')) return true
  if (item.children) {
    return item.children.some((child) => {
      const childPath = `/${lang}/${getLocalizedNavPath(child.to, lang)}`
      return pathname === childPath || pathname.startsWith(childPath + '/')
    })
  }
  return false
}

/** Check if a specific child nav item matches the current path */
function isChildActive(child: NavChild, pathname: string, lang: SupportedLanguage): boolean {
  const childPath = `/${lang}/${getLocalizedNavPath(child.to, lang)}`
  return pathname === childPath || pathname.startsWith(childPath + '/')
}

export function Header() {
  const { t, i18n } = useTranslation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const { pathname } = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileMenuOpen])

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-sticky transition-all duration-normal',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-nav'
          : 'bg-primary'
      )}
    >
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        {t('aria.skipToContent')}
      </a>

      <div className="container-main">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <LocalizedLink to="home" className="flex-shrink-0">
            <img
              src={isScrolled ? '/media/logos/selea-logo-color.svg' : '/media/logos/selea-logo-white.svg'}
              alt="Selea"
              className="h-8 md:h-10 w-auto"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => (
              <div
                key={item.labelKey}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.labelKey)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {(() => {
                  const active = isNavItemActive(item, pathname, currentLang)
                  return (
                    <LocalizedLink
                      to={`/${getLocalizedNavPath(item.to, currentLang)}`}
                      className={cn(
                        'px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 inline-flex items-center gap-1.5 relative',
                        isScrolled
                          ? 'text-text-body hover:text-accent hover:bg-accent/5'
                          : 'text-white/85 hover:text-white hover:bg-white/10',
                        // Active state for open dropdown
                        openDropdown === item.labelKey && item.children && (
                          isScrolled
                            ? 'text-accent bg-accent/5'
                            : 'text-white bg-white/10'
                        ),
                        // Active route state
                        active && (
                          isScrolled ? 'text-accent' : 'text-white'
                        )
                      )}
                    >
                      {t(item.labelKey)}
                      {item.children && (
                        <motion.span
                          animate={{ rotate: openDropdown === item.labelKey ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </motion.span>
                      )}
                      {/* Active indicator — bottom accent line */}
                      {active && (
                        <motion.span
                          layoutId="nav-active-indicator"
                          className={cn(
                            'absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 w-5 rounded-full',
                            isScrolled ? 'bg-accent' : 'bg-white'
                          )}
                          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                        />
                      )}
                    </LocalizedLink>
                  )
                })()}

                {/* Desktop dropdown menu */}
                {item.children && (
                  <AnimatePresence>
                    {openDropdown === item.labelKey && (
                      <motion.div
                        variants={dropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64"
                      >
                        {/* Arrow connector */}
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 -translate-y-full">
                          <div className={cn(
                            'w-3 h-3 rotate-45 border-l border-t',
                            isScrolled
                              ? 'bg-white border-surface-border'
                              : 'bg-primary border-white/10'
                          )} />
                        </div>

                        <div className={cn(
                          'relative rounded-lg shadow-xl overflow-hidden',
                          isScrolled
                            ? 'bg-white ring-1 ring-surface-border'
                            : 'bg-primary ring-1 ring-white/10'
                        )}>
                          {/* Accent top bar */}
                          <div className={cn(
                            'h-0.5 bg-gradient-to-r',
                            isScrolled
                              ? 'from-accent to-accent/30'
                              : 'from-accent to-accent/40'
                          )} />

                          <div className="py-1.5">
                            {item.children.map((child) => {
                              const Icon = child.icon
                              const childActive = isChildActive(child, pathname, currentLang)
                              return (
                                <motion.div key={child.labelKey} variants={dropdownItemVariants}>
                                  <LocalizedLink
                                    to={`/${getLocalizedNavPath(child.to, currentLang)}`}
                                    className={cn(
                                      'group flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 relative',
                                      isScrolled
                                        ? cn(
                                            'hover:bg-surface-light hover:text-accent',
                                            childActive ? 'text-accent bg-surface-light' : 'text-text-body'
                                          )
                                        : cn(
                                            'hover:bg-white/10 hover:text-white',
                                            childActive ? 'text-white bg-white/5' : 'text-white/70'
                                          )
                                    )}
                                  >
                                    {/* Left accent line — always visible when active, on hover otherwise */}
                                    <span className={cn(
                                      'absolute left-0 top-1/2 -translate-y-1/2 w-0.5 bg-accent rounded-r transition-all duration-200',
                                      childActive ? 'h-5' : 'h-0 group-hover:h-5'
                                    )} />
                                    {Icon && (
                                      <span className={cn(
                                        'flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-all duration-200 group-hover:scale-105',
                                        isScrolled
                                          ? cn(
                                              'group-hover:bg-accent/10',
                                              childActive ? 'bg-accent/10' : 'bg-surface-light'
                                            )
                                          : cn(
                                              'group-hover:bg-white/15',
                                              childActive ? 'bg-white/15' : 'bg-white/10'
                                            )
                                      )}>
                                        <Icon className={cn(
                                          'w-4 h-4 transition-colors duration-200',
                                          isScrolled
                                            ? cn(
                                                'group-hover:text-accent',
                                                childActive ? 'text-accent' : 'text-text-muted'
                                              )
                                            : cn(
                                                'group-hover:text-white',
                                                childActive ? 'text-white' : 'text-white/60'
                                              )
                                        )} />
                                      </span>
                                    )}
                                    <span className="font-medium">{t(child.labelKey)}</span>
                                  </LocalizedLink>
                                </motion.div>
                              )
                            })}
                          </div>

                          {/* "View all" footer link */}
                          <div className={cn(
                            'border-t',
                            isScrolled ? 'border-surface-border' : 'border-white/10'
                          )}>
                            <LocalizedLink
                              to={`/${getLocalizedNavPath(item.to, currentLang)}`}
                              className={cn(
                                'flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors uppercase tracking-wider',
                                isScrolled
                                  ? 'text-text-muted hover:text-accent'
                                  : 'text-white/50 hover:text-white'
                              )}
                            >
                              {t(item.labelKey)}
                              <ChevronDown className="w-3 h-3 -rotate-90" />
                            </LocalizedLink>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}

            <LocalizedLink
              to={`/${getLocalizedNavPath('accedi', currentLang)}`}
              className={cn(
                'ml-3 px-4 py-1.5 text-sm font-medium rounded-md border transition-all duration-200 inline-flex items-center gap-1.5',
                isScrolled
                  ? 'border-accent text-accent hover:bg-accent hover:text-white'
                  : 'border-white/50 text-white hover:bg-white/10 hover:border-white'
              )}
            >
              <LogIn className="w-3.5 h-3.5" />
              {t('auth.signIn')}
            </LocalizedLink>

            <div className="ml-3 pl-3 border-l border-white/20">
              <LanguageSelector variant="desktop" isScrolled={isScrolled} />
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={cn(
              'lg:hidden p-2 rounded-md transition-colors',
              isScrolled ? 'text-text-body hover:bg-surface-light' : 'text-white hover:bg-white/10'
            )}
            aria-label={mobileMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="lg:hidden bg-white border-t border-surface-border overflow-hidden max-h-[calc(100vh-64px)] overflow-y-auto"
          >
            <div className="py-3">
              {navItems.map((item) => {
                const active = isNavItemActive(item, pathname, currentLang)
                return (
                  <div key={item.labelKey}>
                    {item.children ? (
                      <>
                        {/* Accordion toggle for items with children */}
                        <button
                          onClick={() => setMobileExpanded(mobileExpanded === item.labelKey ? null : item.labelKey)}
                          className={cn(
                            'w-full flex items-center justify-between px-6 py-3 font-medium hover:bg-surface-light transition-colors relative',
                            active ? 'text-accent' : 'text-text-body'
                          )}
                        >
                          {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-accent rounded-r" />}
                          <span>{t(item.labelKey)}</span>
                          <motion.span
                            animate={{ rotate: mobileExpanded === item.labelKey ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className={cn('w-4 h-4', active ? 'text-accent' : 'text-text-muted')} />
                          </motion.span>
                        </button>

                        <AnimatePresence>
                          {mobileExpanded === item.labelKey && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                              className="overflow-hidden bg-primary"
                            >
                              {/* Parent "view all" link */}
                              <LocalizedLink
                                to={`/${getLocalizedNavPath(item.to, currentLang)}`}
                                className="flex items-center gap-3 px-8 py-2.5 text-sm text-white font-semibold hover:bg-white/10 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                <span className="w-1 h-1 rounded-full bg-accent" />
                                {t(item.labelKey)}
                              </LocalizedLink>

                              {item.children.map((child) => {
                                const Icon = child.icon
                                const childActive = isChildActive(child, pathname, currentLang)
                                return (
                                  <LocalizedLink
                                    key={child.labelKey}
                                    to={`/${getLocalizedNavPath(child.to, currentLang)}`}
                                    className={cn(
                                      'flex items-center gap-3 px-8 py-2.5 text-sm hover:text-white hover:bg-white/10 transition-colors relative',
                                      childActive ? 'text-white bg-white/5' : 'text-white/75'
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {childActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-accent rounded-r" />}
                                    {Icon && (
                                      <span className={cn(
                                        'flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center',
                                        childActive ? 'bg-white/15' : 'bg-white/10'
                                      )}>
                                        <Icon className={cn('w-3.5 h-3.5', childActive ? 'text-white' : 'text-white/60')} />
                                      </span>
                                    )}
                                    <span>{t(child.labelKey)}</span>
                                  </LocalizedLink>
                                )
                              })}

                              <div className="h-px bg-white/10 mx-6 my-1" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <LocalizedLink
                        to={`/${getLocalizedNavPath(item.to, currentLang)}`}
                        className={cn(
                          'block px-6 py-3 font-medium hover:bg-surface-light transition-colors relative',
                          active ? 'text-accent' : 'text-text-body'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-accent rounded-r" />}
                        {t(item.labelKey)}
                      </LocalizedLink>
                    )}
                  </div>
                )
              })}
              <div className="px-6 py-3">
                <LocalizedLink
                  to={`/${getLocalizedNavPath('accedi', currentLang)}`}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-accent border border-accent rounded-md hover:bg-accent hover:text-white transition-colors w-full justify-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LogIn className="w-4 h-4" />
                  {t('auth.signIn')}
                </LocalizedLink>
              </div>
              <div className="border-t border-surface-border pt-2">
                <LanguageSelector variant="mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
